import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '../../../lib/utils';
import { Card } from '../../../components/ui/card';
import { CashflowChartsProps } from '../types';

/** Палитра линий по провайдерам; для незнакомого кода — запасной цвет. */
const PROVIDER_COLORS: Record<string, string> = {
  click: '#22d3ee',
  paylov: '#f59e0b',
  payme: '#818cf8',
  apple: '#e879f9',
};
const FALLBACK_COLOR = '#94a3b8';

const compact = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} млн`;
  if (Math.abs(value) >= 1_000) return `${Math.round(value / 1_000)} тыс.`;
  return String(value);
};

export function CashflowCharts({
  isDark,
  providers,
  daily,
  formatMoney,
}: CashflowChartsProps) {
  const gridColor = isDark ? '#334155' : '#e2e8f0';
  const axisColor = isDark ? '#94a3b8' : '#64748b';

  // Провайдеры считаются в разных валютах, поэтому на графике — сумовые
  // эквиваленты: иначе доллары Apple встали бы рядом с сумами как равные.
  const byProvider = useMemo(
    () =>
      providers.map((p) => ({
        name: p.name,
        Чистое: p.netUzs,
        Поступило: p.settledUzs,
        Осталось: p.outstandingUzs,
      })),
    [providers],
  );

  // Точки графика: одна строка на дату, колонка на каждого провайдера.
  const timeline = useMemo(() => {
    const byDate = new Map<string, Record<string, number | string>>();
    daily.forEach((point) => {
      const row = byDate.get(point.date) || { date: point.date };
      row[point.code] = ((row[point.code] as number) || 0) + point.grossUzs;
      byDate.set(point.date, row);
    });
    return Array.from(byDate.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
  }, [daily]);

  // Внутри вкладки своя тень не нужна — иначе карточка в карточке.
  const cardCls = cn(
    'rounded-2xl border p-5 lg:p-6 transition-all duration-300 shadow-none',
    isDark ? 'bg-transparent border-slate-700' : 'bg-transparent border-slate-200',
  );
  const titleCls = cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#111111]');

  const tooltipStyle = {
    backgroundColor: isDark ? '#1A1A22' : '#ffffff',
    border: `1px solid ${gridColor}`,
    borderRadius: '12px',
    color: isDark ? '#ffffff' : '#111111',
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className={cardCls}>
        <h3 className={titleCls}>По провайдерам</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={byProvider}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="name" stroke={axisColor} tickLine={false} axisLine={false} />
            <YAxis stroke={axisColor} tickFormatter={compact} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value) => formatMoney(Number(value) || 0)}
            />
            <Legend />
            <Bar dataKey="Чистое" fill="#34d399" radius={[6, 6, 0, 0]} />
            <Bar dataKey="Поступило" fill="#22d3ee" radius={[6, 6, 0, 0]} />
            <Bar dataKey="Осталось" fill="#f59e0b" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className={cardCls}>
        <h3 className={titleCls}>Собрано по дням</h3>
        {timeline.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-slate-500 text-sm">
            За выбранный период платежей нет
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="date" stroke={axisColor} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis stroke={axisColor} tickFormatter={compact} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value) => formatMoney(Number(value) || 0)}
              />
              <Legend />
              {providers.map((provider) => (
                <Line
                  key={provider.code}
                  type="monotone"
                  dataKey={provider.code}
                  name={provider.name}
                  stroke={PROVIDER_COLORS[provider.code] || FALLBACK_COLOR}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}
